import { useEffect, useRef, useState } from "react";
import { ArrowUpDown, ChevronDown, ChevronRight, ChevronUp, Download, ExternalLink, Layers, List, SlidersHorizontal } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function formatDateSafe(isoStr) {
  if (!isoStr) return null;
  const datePart = isoStr.split("T")[0];
  if (!datePart) return isoStr;
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

// ── Flat mode columns ─────────────────────────────────────────────────────────

const COLUMNS = [
  { key: "deal_name",         label: "Deal",        sortable: true,  alwaysVisible: true  },
  { key: "company_name",      label: "Company",     sortable: true,  alwaysVisible: false },
  { key: "product",           label: "Product",     sortable: true,  alwaysVisible: false },
  { key: "line_item_name",    label: "Line item",   sortable: true,  alwaysVisible: false },
  { key: "line_item_price",   label: "Price",       sortable: true,  alwaysVisible: false, numeric: true },
  { key: "monthly_mrr",       label: "MRR",         sortable: true,  alwaysVisible: true,  numeric: true },
  { key: "revenue_kind",      label: "Kind",        sortable: true,  alwaysVisible: false },
  { key: "billing_frequency", label: "Billing",     sortable: true,  alwaysVisible: false },
  { key: "deal_stage",        label: "Stage",       sortable: true,  alwaysVisible: false },
  { key: "probability",       label: "Probability", sortable: true,  alwaysVisible: false, numeric: true },
  { key: "csm",               label: "CSM",         sortable: true,  alwaysVisible: false },
  { key: "owner",             label: "Owner",       sortable: true,  alwaysVisible: false },
];

const ALL_COLUMN_KEYS = COLUMNS.map((c) => c.key);
const STORAGE_KEY = "portaliq_drilldown_columns";
const DEFAULT_COLUMN_KEYS = [
  "deal_name", "company_name", "product", "line_item_name",
  "line_item_price", "monthly_mrr", "deal_stage", "csm",
];

function loadSavedColumns() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMN_KEYS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_COLUMN_KEYS;
    const alwaysVisible = COLUMNS.filter((c) => c.alwaysVisible).map((c) => c.key);
    const merged = [...new Set([...alwaysVisible, ...parsed.filter((k) => ALL_COLUMN_KEYS.includes(k))])];
    return merged;
  } catch {
    return DEFAULT_COLUMN_KEYS;
  }
}

// ── Grouping helper ───────────────────────────────────────────────────────────

function groupByDeal(records) {
  const map = new Map();
  for (const r of records) {
    const key = r.deal_id ?? r.deal_name;
    if (!map.has(key)) {
      map.set(key, {
        deal_id: r.deal_id,
        deal_name: r.deal_name,
        deal_url: r.deal_url,
        company_name: r.company_name,
        deal_stage: r.deal_stage,
        probability: r.probability,
        owner: r.owner,
        change_type: r.change_type,
        total_mrr: 0,
        items: [],
      });
    }
    const g = map.get(key);
    g.total_mrr = Math.round((g.total_mrr + (r.monthly_mrr || 0)) * 100) / 100;
    g.items.push(r);
  }
  return [...map.values()].map((g) => {
    const proj = g.items.filter((i) => i.revenue_kind === "projected").length;
    g.revenue_kind = proj === 0 ? "actual" : proj === g.items.length ? "projected" : "mixed";
    const xero = g.items.filter((i) => i.xero_linked).length;
    g.xero_state = xero === 0 ? "none" : xero === g.items.length ? "all" : "some";
    return g;
  });
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="ml-1 inline h-3 w-3 text-slate-300" />;
  return dir === "asc"
    ? <ChevronUp className="ml-1 inline h-3 w-3 text-[#119996]" />
    : <ChevronDown className="ml-1 inline h-3 w-3 text-[#119996]" />;
}

function ChangesBadge({ type }) {
  if (!type) return null;
  const isNew = type === "new";
  return (
    <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${isNew ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-rose-50 text-rose-600 ring-1 ring-rose-200"}`}>
      {isNew ? "New" : "Ended"}
    </span>
  );
}

function ProjectedBadge({ kind }) {
  if (kind !== "projected" && kind !== "mixed") return null;
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-600 ring-1 ring-violet-200">
      {kind === "mixed" ? "Part projected" : "Projected"}
    </span>
  );
}

// state: boolean (per line item) or "all" | "some" | "none" (per deal group).
function XeroBadge({ state }) {
  const s = state === true ? "all" : state === false ? "none" : state;
  if (s === "all")
    return <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200">Xero</span>;
  if (s === "some")
    return <span className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">Part Xero</span>;
  return <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-300">Non-Xero</span>;
}

function ColumnPicker({ visibleColumns, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function toggle(key) {
    const col = COLUMNS.find((c) => c.key === key);
    if (col?.alwaysVisible) return;
    const next = visibleColumns.includes(key)
      ? visibleColumns.filter((k) => k !== key)
      : [...visibleColumns, key];
    onChange(next);
  }

  const hiddenCount = COLUMNS.filter((c) => !c.alwaysVisible && !visibleColumns.includes(c.key)).length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ${open ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]" : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"}`}
        data-testid="drilldown-columns-button"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Columns
        {hiddenCount > 0 && (
          <span className="rounded-full bg-[#50e3c2] px-1.5 py-px text-[10px] font-semibold text-[#323c44]">{hiddenCount}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-52 rounded-md border border-[#dddddd] bg-white py-1 shadow-lg" data-testid="drilldown-columns-picker">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#7d8b96]">Visible columns</p>
          {COLUMNS.map((col) => {
            const checked = visibleColumns.includes(col.key);
            return (
              <label key={col.key} className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm transition hover:bg-[#f5f5f5] ${col.alwaysVisible ? "cursor-default opacity-50" : ""}`} data-testid={`drilldown-col-toggle-${col.key}`}>
                <input type="checkbox" checked={checked} disabled={col.alwaysVisible} onChange={() => toggle(col.key)} className="h-3.5 w-3.5 accent-[#50e3c2]" />
                <span className="text-[#323c44]">{col.label}</span>
                {col.alwaysVisible && <span className="ml-auto text-[10px] text-[#7d8b96]">Always</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Grouped table ─────────────────────────────────────────────────────────────

function GroupedTable({ groups, expandedDeals, onToggle, formatCurrency, sortKey, sortDir, onSort, activeColumns }) {

  // Render a single cell for a deal-level grouped row.
  // Line-item-specific fields (product, line_item_name, etc.) use items[0] for
  // single-item deals; multi-item deals show "—" — the user expands to see detail.
  function renderGroupCell(col, group) {
    const isEnded = group.change_type === "ended";
    const isProjected = group.revenue_kind === "projected";
    const firstItem = group.items[0];
    const isSingle = group.items.length === 1;

    switch (col.key) {
      case "deal_name":
        return (
          <TableCell key={col.key} className="min-w-[200px]">
            <div className="flex flex-wrap items-center gap-1">
              <a
                href={group.deal_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-[#323c44] underline-offset-4 hover:text-[#119996] hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {group.deal_name}
                <ExternalLink className="h-3 w-3" />
              </a>
              <ChangesBadge type={group.change_type} />
              <ProjectedBadge kind={group.revenue_kind} />
              <XeroBadge state={group.xero_state} />
              {group.items.length > 1 && (
                <span className="rounded-full bg-[#efefef] px-1.5 py-0.5 text-[10px] font-semibold text-[#4a5b68]">
                  {group.items.length} items
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-[#7d8b96]">ID {group.deal_id}</p>
          </TableCell>
        );
      case "company_name":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{group.company_name || "—"}</TableCell>;
      case "product":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{isSingle ? (firstItem?.product || "—") : "—"}</TableCell>;
      case "line_item_name":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{isSingle ? (firstItem?.line_item_name || "—") : "—"}</TableCell>;
      case "line_item_price":
        return <TableCell key={col.key} className="font-mono text-sm text-[#4a5b68]">{isSingle ? formatCurrency(firstItem?.line_item_price) : "—"}</TableCell>;
      case "monthly_mrr":
        return (
          <TableCell key={col.key} className={`text-right font-mono font-semibold ${isEnded ? "text-rose-500" : isProjected ? "text-violet-600" : "text-[#323c44]"}`}>
            {isEnded ? formatCurrency(0) : formatCurrency(group.total_mrr)}
          </TableCell>
        );
      case "billing_frequency":
        return (
          <TableCell key={col.key} className="text-xs text-[#4a5b68]">
            {isSingle ? (
              <>
                <p>{firstItem?.billing_frequency}</p>
                <p className="text-[#7d8b96]">
                  {firstItem?.billing_start_date ? formatDateSafe(firstItem.billing_start_date) : "—"}
                  {" – "}
                  {firstItem?.billing_end_date ? formatDateSafe(firstItem.billing_end_date) : "Open"}
                </p>
              </>
            ) : "—"}
          </TableCell>
        );
      case "revenue_kind":
        return (
          <TableCell key={col.key} className="text-sm">
            {group.revenue_kind === "actual"
              ? <span className="text-[#4a5b68]">Actual</span>
              : <span className="text-violet-600 font-medium">{group.revenue_kind === "mixed" ? "Mixed" : "Projected"}</span>}
          </TableCell>
        );
      case "deal_stage":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{group.deal_stage || "—"}</TableCell>;
      case "probability":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{group.probability ?? 0}%</TableCell>;
      case "owner":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{group.owner || "—"}</TableCell>;
      case "csm":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{firstItem?.csm || "—"}</TableCell>;
      default:
        return <TableCell key={col.key}>—</TableCell>;
    }
  }

  // Render a line-item sub-row inside an expanded deal group.
  // Deal-level columns (company, stage, owner…) are left blank to avoid duplication.
  function renderSubCell(col, item, isFirst) {
    switch (col.key) {
      case "deal_name":
        return (
          <TableCell key={col.key} className="py-2 pl-6">
            <p className="text-sm font-medium text-[#323c44]">
              {item.line_item_name}
              <ProjectedBadge kind={item.revenue_kind} />
              <XeroBadge state={!!item.xero_linked} />
            </p>
            <p className="text-xs text-[#7d8b96]">{item.product}</p>
          </TableCell>
        );
      case "company_name":   return <TableCell key={col.key} className="py-2" />;
      case "product":        return <TableCell key={col.key} className="py-2 text-sm text-[#4a5b68]">{item.product || "—"}</TableCell>;
      case "line_item_name": return <TableCell key={col.key} className="py-2 text-sm text-[#4a5b68]">{item.line_item_name || "—"}</TableCell>;
      case "line_item_price":return <TableCell key={col.key} className="py-2 font-mono text-sm text-[#4a5b68]">{formatCurrency(item.line_item_price)}</TableCell>;
      case "monthly_mrr":
        return (
          <TableCell key={col.key} className={`py-2 text-right font-mono text-sm ${item.revenue_kind === "projected" ? "text-violet-600" : "text-[#323c44]"}`}>
            {formatCurrency(item.monthly_mrr)}
          </TableCell>
        );
      case "billing_frequency":
        return (
          <TableCell key={col.key} className="py-2 text-xs text-[#4a5b68]">
            <p>{item.billing_frequency}</p>
            <p className="text-[#7d8b96]">
              {item.billing_start_date ? formatDateSafe(item.billing_start_date) : "—"}
              {" – "}
              {item.billing_end_date ? formatDateSafe(item.billing_end_date) : "Open"}
            </p>
          </TableCell>
        );
      case "revenue_kind":
        return (
          <TableCell key={col.key} className="py-2 text-sm">
            {item.revenue_kind === "projected"
              ? <span className="text-violet-600">Projected</span>
              : <span className="text-[#4a5b68]">Actual</span>}
          </TableCell>
        );
      case "deal_stage":   return <TableCell key={col.key} className="py-2" />;
      case "probability":  return <TableCell key={col.key} className="py-2" />;
      case "owner":        return <TableCell key={col.key} className="py-2" />;
      case "csm":          return <TableCell key={col.key} className="py-2 text-sm text-[#4a5b68]">{item.csm || "—"}</TableCell>;
      default:             return <TableCell key={col.key} className="py-2" />;
    }
  }

  return (
    <div className="overflow-hidden rounded-md border border-[#cccccc]" data-testid="month-drilldown-grouped-table">
      <Table>
        <TableHeader className="bg-[#f5f5f5]">
          <TableRow>
            {/* chevron / expand column */}
            <TableHead className="w-8" />
            {activeColumns.map((col) => (
              <TableHead
                key={col.key}
                className={col.sortable ? "cursor-pointer select-none hover:bg-[#efefef]" : ""}
                onClick={col.sortable ? () => onSort(col.key) : undefined}
              >
                <span className="whitespace-nowrap">
                  {col.label}
                  {col.sortable && <SortIcon active={sortKey === col.key} dir={sortDir} />}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const isExpanded = expandedDeals.has(group.deal_id ?? group.deal_name);
            const hasMultiple = group.items.length > 1;
            const isEnded = group.change_type === "ended";
            const isProjected = group.revenue_kind === "projected";
            return (
              <>
                {/* Deal row */}
                <TableRow
                  key={`deal-${group.deal_id}`}
                  className={`cursor-pointer hover:bg-[#f5f5f5] ${isEnded ? "opacity-60" : ""} ${isProjected ? "bg-violet-50/40" : ""}`}
                  onClick={() => hasMultiple && onToggle(group.deal_id ?? group.deal_name)}
                  data-testid={`month-drilldown-group-${group.deal_id}`}
                >
                  <TableCell className="w-8 pr-0 text-center">
                    {hasMultiple ? (
                      <span className="text-[#7d8b96]">
                        {isExpanded
                          ? <ChevronDown className="h-3.5 w-3.5" />
                          : <ChevronRight className="h-3.5 w-3.5" />}
                      </span>
                    ) : (
                      <span className="block h-3.5 w-3.5" />
                    )}
                  </TableCell>
                  {activeColumns.map((col) => renderGroupCell(col, group))}
                </TableRow>

                {/* Line item sub-rows (expanded) */}
                {isExpanded && group.items.map((item, idx) => (
                  <TableRow
                    key={`item-${group.deal_id}-${idx}`}
                    className="bg-[#f5f5f5] hover:bg-[#f0f6ff]"
                    data-testid={`month-drilldown-item-${group.deal_id}-${idx}`}
                  >
                    <TableCell className="w-8 border-l-2 border-[#57c1a9]/20 py-2" />
                    {activeColumns.map((col) => renderSubCell(col, item, idx === 0))}
                  </TableRow>
                ))}
              </>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DrilldownSheet({ open, onOpenChange, detail, loading, formatCurrency, formatMonthLabel, onRefetch }) {
  const [sortKey, setSortKey] = useState("monthly_mrr");
  const [sortDir, setSortDir] = useState("desc");
  const [changesOnly, setChangesOnly] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(loadSavedColumns);
  const [groupedMode, setGroupedMode] = useState(true);
  const [expandedDeals, setExpandedDeals] = useState(new Set());
  const [kindFilter, setKindFilter] = useState("all");   // "all" | "actual" | "projected"
  const [xeroFilter, setXeroFilter] = useState("all");   // "all" | "xero" | "non-xero"

  const windowMode = detail?.month === "window";   // whole-period "all line items" view

  const handleColumnsChange = (next) => {
    setVisibleColumns(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const toggleDealExpand = (dealId) => {
    setExpandedDeals((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const handleChangesToggle = () => {
    const next = !changesOnly;
    setChangesOnly(next);
    onRefetch?.(next);
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) { setChangesOnly(false); setExpandedDeals(new Set()); setKindFilter("all"); setXeroFilter("all"); }
    onOpenChange(isOpen);
  };

  const allRecords = detail?.records || [];
  const records = allRecords.filter((r) =>
    (kindFilter === "all" || (r.revenue_kind || "actual") === kindFilter) &&
    (xeroFilter === "all" || (xeroFilter === "xero" ? !!r.xero_linked : !r.xero_linked))
  );
  const projectedCount = allRecords.filter((r) => r.revenue_kind === "projected").length;
  const nonXeroCount = allRecords.filter((r) => !r.xero_linked).length;

  // Flat sorted records (used in flat mode)
  const sortedRecords = [...records].sort((a, b) => {
    if (changesOnly) {
      const aEnded = a.change_type === "ended" ? 1 : 0;
      const bEnded = b.change_type === "ended" ? 1 : 0;
      if (aEnded !== bEnded) return aEnded - bEnded;
    }
    let av = a[sortKey] ?? "";
    let bv = b[sortKey] ?? "";
    if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
    av = String(av).toLowerCase();
    bv = String(bv).toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Grouped + sorted deal groups (used in grouped mode)
  const sortedGroups = groupByDeal(records).sort((a, b) => {
    // map monthly_mrr → total_mrr for group sorting
    const aVal = sortKey === "monthly_mrr" ? a.total_mrr : (a[sortKey] ?? "");
    const bVal = sortKey === "monthly_mrr" ? b.total_mrr : (b[sortKey] ?? "");
    if (typeof aVal === "number" && typeof bVal === "number") return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    const as = String(aVal).toLowerCase();
    const bs = String(bVal).toLowerCase();
    if (as < bs) return sortDir === "asc" ? -1 : 1;
    if (as > bs) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const newCount   = records.filter((r) => r.change_type === "new").length;
  const endedCount = records.filter((r) => r.change_type === "ended").length;
  const activeColumns = COLUMNS.filter((c) => visibleColumns.includes(c.key));

  const handleExportCSV = () => {
    if (!sortedRecords.length) return;
    const cols = [
      { key: "deal_id",            label: "Deal ID"            },
      { key: "deal_name",          label: "Deal Name"          },
      { key: "company_name",       label: "Company"            },
      { key: "product",            label: "Product"            },
      { key: "line_item_name",     label: "Line Item Name"     },
      { key: "line_item_price",    label: "Line Item Price"    },
      { key: "monthly_mrr",        label: "Monthly MRR"        },
      { key: "revenue_kind",       label: "Revenue Kind"       },
      { key: "xero_linked",        label: "Xero Linked"        },
      { key: "billing_frequency",  label: "Billing Frequency"  },
      { key: "billing_start_date", label: "Billing Start Date" },
      { key: "billing_end_date",   label: "Billing End Date"   },
      { key: "deal_stage",         label: "Deal Stage"         },
      { key: "probability",        label: "Probability %"      },
      { key: "owner",              label: "Owner"              },
      { key: "csm",                label: "CSM"                },
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map((c) => c.label).join(",");
    const body = sortedRecords.map((r) => cols.map((c) => escape(r[c.key])).join(","));
    const csv = [header, ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mrr-${detail?.month || "export"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  function renderCell(col, record) {
    const isEnded = record.change_type === "ended";
    const isProjected = record.revenue_kind === "projected";
    switch (col.key) {
      case "deal_name":
        return (
          <TableCell key={col.key} className="min-w-[210px]">
            <div className="flex flex-wrap items-center gap-1">
              <a href={record.deal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-medium text-[#323c44] underline-offset-4 hover:text-[#119996] hover:underline" data-testid={`month-drilldown-deal-link-${record.deal_id}`}>
                {record.deal_name}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <ChangesBadge type={record.change_type} />
              <ProjectedBadge kind={record.revenue_kind} />
              <XeroBadge state={!!record.xero_linked} />
            </div>
            <p className="mt-1 text-xs text-[#4a5b68]" data-testid={`month-drilldown-deal-id-${record.deal_id}`}>ID {record.deal_id}</p>
          </TableCell>
        );
      case "company_name":   return <TableCell key={col.key}>{record.company_name || "—"}</TableCell>;
      case "product":        return <TableCell key={col.key}>{record.product}</TableCell>;
      case "line_item_name": return <TableCell key={col.key}>{record.line_item_name}</TableCell>;
      case "line_item_price":return <TableCell key={col.key} className="font-mono">{formatCurrency(record.line_item_price)}</TableCell>;
      case "monthly_mrr":
        return (
          <TableCell key={col.key} className={`font-mono ${isEnded ? "text-rose-500" : isProjected ? "text-violet-600" : "text-[#323c44]"}`}>
            {isEnded ? formatCurrency(0) : formatCurrency(record.monthly_mrr)}
          </TableCell>
        );
      case "billing_frequency":
        return (
          <TableCell key={col.key}>
            <div className="space-y-1 text-xs text-[#4a5b68]">
              <p>{record.billing_frequency}</p>
              <p>{record.billing_start_date ? formatDateSafe(record.billing_start_date) : "—"}</p>
              <p>{record.billing_end_date ? formatDateSafe(record.billing_end_date) : "Open ended"}</p>
            </div>
          </TableCell>
        );
      case "revenue_kind":
        return (
          <TableCell key={col.key}>
            {isProjected
              ? <span className="text-violet-600 font-medium">Projected</span>
              : <span className="text-[#4a5b68]">Actual</span>}
          </TableCell>
        );
      case "deal_stage":   return <TableCell key={col.key}>{record.deal_stage || "—"}</TableCell>;
      case "probability":  return <TableCell key={col.key}>{record.probability ?? 0}%</TableCell>;
      case "owner":        return <TableCell key={col.key}>{record.owner || "—"}</TableCell>;
      case "csm":          return <TableCell key={col.key}>{record.csm || "—"}</TableCell>;
      default:             return null;
    }
  }

  const isEmpty = groupedMode ? sortedGroups.length === 0 : sortedRecords.length === 0;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto border-l border-[#cccccc] bg-white p-0 sm:max-w-[78rem]" data-testid="month-drilldown-sheet">
        <div className="sticky top-0 z-10 border-b border-[#cccccc] bg-[#f5f5f5] px-6 py-5">
          <SheetHeader>
            <SheetTitle className="font-sans text-2xl text-[#323c44]" data-testid="month-drilldown-title">
              {detail
                ? windowMode
                  ? `${detail.label} — all line items`
                  : `${formatMonthLabel(detail.month)} drill-down`
                : "Month drill-down"}
            </SheetTitle>
            <SheetDescription className="text-sm text-[#4a5b68]">
              {windowMode
                ? "Every line item active anywhere in this period — one row per line. Filter to Non-Xero to audit revenue not yet tied to a Xero invoice."
                : changesOnly
                ? "Showing line items that started or ended this month."
                : "Audit every contributing deal and line item. Click deal rows to expand individual line items."}
            </SheetDescription>
          </SheetHeader>

          {detail && (
            <div className={`mt-5 grid gap-3 ${windowMode ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
              <div className="rounded-md border border-[#cccccc] bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">{windowMode ? "Period total" : "Month total"}</p>
                <p className="mt-3 font-sans text-3xl text-[#323c44]">{formatCurrency(detail.total_mrr)}</p>
                {detail.projected_mrr > 0 && (
                  <p className="mt-1.5 text-xs text-[#4a5b68]">
                    <span className="font-semibold text-[#323c44]">{formatCurrency(detail.actual_mrr)}</span> actual
                    {" · "}
                    <span className="font-semibold text-violet-600">{formatCurrency(detail.projected_mrr)}</span> projected
                  </p>
                )}
              </div>
              <div className="rounded-md border border-[#cccccc] bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">
                  {groupedMode ? "Deals" : "Line items"}
                </p>
                <p className="mt-3 font-sans text-3xl text-[#323c44]">
                  {groupedMode ? sortedGroups.length : records.length}
                  {groupedMode && records.length !== sortedGroups.length && (
                    <span className="ml-2 text-base font-normal text-[#7d8b96]">({records.length} items)</span>
                  )}
                </p>
              </div>
              {!windowMode && (
              <div
                className={`flex cursor-pointer items-center justify-between rounded-md border p-4 transition ${changesOnly ? "border-[#57c1a9] bg-[#e8faf5]" : "border-[#cccccc] bg-white hover:bg-[#f5f5f5]"}`}
                onClick={handleChangesToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleChangesToggle()}
                data-testid="changes-toggle"
              >
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${changesOnly ? "text-[#119996]" : "text-[#4a5b68]"}`}>Changes only</p>
                  {changesOnly
                    ? <p className="mt-1 text-xs text-[#119996]">{newCount} new · {endedCount} ended</p>
                    : <p className="mt-1 text-xs text-[#7d8b96]">New starts + expired items</p>}
                </div>
                <div className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors ${changesOnly ? "bg-[#50e3c2]" : "bg-[#cccccc]"}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${changesOnly ? "translate-x-4" : "translate-x-0.5"}`} />
                </div>
              </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="rounded-md border border-[#cccccc] bg-[#f5f5f5] px-6 py-16 text-center text-sm text-[#4a5b68]">Loading month details...</div>
          ) : detail ? (
            isEmpty ? (
              <div className="rounded-md border border-dashed border-[#cccccc] bg-[#f5f5f5] px-6 py-16 text-center text-sm text-[#4a5b68]">
                {windowMode ? "No line items active in this period." : changesOnly ? "No line items started or ended this month." : "No records for this month."}
              </div>
            ) : (
              <>
                {/* Toolbar */}
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-xs text-[#7d8b96]">
                    {groupedMode
                      ? `${sortedGroups.length} deal${sortedGroups.length !== 1 ? "s" : ""}, ${records.length} line item${records.length !== 1 ? "s" : ""}`
                      : `${records.length} record${records.length !== 1 ? "s" : ""}`}
                  </p>
                  <div className="flex items-center gap-2">
                    {/* Actual / Projected filter — only when there's projected revenue */}
                    {projectedCount > 0 && (
                      <div className="flex overflow-hidden rounded-md border border-[#dddddd]">
                        {[["all", "All"], ["actual", "Actual"], ["projected", "Projected"]].map(([val, label], i) => (
                          <button
                            key={val}
                            type="button"
                            onClick={() => setKindFilter(val)}
                            className={`px-3 py-1.5 text-xs font-medium transition ${i > 0 ? "border-l border-[#dddddd]" : ""} ${
                              kindFilter === val
                                ? (val === "projected" ? "bg-violet-50 text-violet-600" : "bg-[#e8faf5] text-[#119996]")
                                : "bg-white text-[#4a5b68] hover:bg-[#efefef]"
                            }`}
                            title={`Show ${label.toLowerCase()} revenue`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Xero / Non-Xero filter — only when some revenue isn't Xero-backed */}
                    {nonXeroCount > 0 && (
                      <div className="flex overflow-hidden rounded-md border border-[#dddddd]">
                        {[["all", "All"], ["xero", "Xero"], ["non-xero", "Non-Xero"]].map(([val, label], i) => (
                          <button
                            key={val}
                            type="button"
                            onClick={() => setXeroFilter(val)}
                            className={`px-3 py-1.5 text-xs font-medium transition ${i > 0 ? "border-l border-[#dddddd]" : ""} ${
                              xeroFilter === val
                                ? (val === "xero" ? "bg-emerald-50 text-emerald-700" : val === "non-xero" ? "bg-amber-100 text-amber-800" : "bg-[#e8faf5] text-[#119996]")
                                : "bg-white text-[#4a5b68] hover:bg-[#efefef]"
                            }`}
                            title={val === "non-xero" ? "Show revenue NOT backed by a Xero invoice (manual/forecast)" : `Show ${label} revenue`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Grouped / Flat toggle */}
                    <div className="flex overflow-hidden rounded-md border border-[#dddddd]">
                      <button
                        type="button"
                        onClick={() => setGroupedMode(true)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${groupedMode ? "bg-[#e8faf5] text-[#119996]" : "bg-white text-[#4a5b68] hover:bg-[#efefef]"}`}
                        title="Group by deal"
                      >
                        <Layers className="h-3.5 w-3.5" />
                        Grouped
                      </button>
                      <button
                        type="button"
                        onClick={() => setGroupedMode(false)}
                        className={`flex items-center gap-1.5 border-l border-[#dddddd] px-3 py-1.5 text-xs font-medium transition ${!groupedMode ? "bg-[#e8faf5] text-[#119996]" : "bg-white text-[#4a5b68] hover:bg-[#efefef]"}`}
                        title="Show flat list"
                      >
                        <List className="h-3.5 w-3.5" />
                        Flat
                      </button>
                    </div>
                    {/* Columns picker — available in both modes */}
                    <ColumnPicker visibleColumns={visibleColumns} onChange={handleColumnsChange} />
                    {/* Export all records as CSV */}
                    <button
                      type="button"
                      onClick={handleExportCSV}
                      className="flex items-center gap-1.5 rounded-md border border-[#dddddd] bg-white px-3 py-1.5 text-xs font-medium text-[#4a5b68] transition hover:bg-[#efefef]"
                      title="Export all records to CSV"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Export CSV
                    </button>
                  </div>
                </div>

                {groupedMode ? (
                  <GroupedTable
                    groups={sortedGroups}
                    expandedDeals={expandedDeals}
                    onToggle={toggleDealExpand}
                    formatCurrency={formatCurrency}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                    activeColumns={activeColumns}
                  />
                ) : (
                  <div className="overflow-hidden rounded-md border border-[#cccccc]" data-testid="month-drilldown-table-wrapper">
                    <Table>
                      <TableHeader className="bg-[#f5f5f5]">
                        <TableRow>
                          {activeColumns.map((col) => (
                            <TableHead key={col.key} className={col.sortable ? "cursor-pointer select-none hover:bg-[#efefef]" : ""} onClick={col.sortable ? () => handleSort(col.key) : undefined}>
                              <span className="whitespace-nowrap">{col.label}{col.sortable && <SortIcon active={sortKey === col.key} dir={sortDir} />}</span>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedRecords.map((record) => (
                          <TableRow
                            key={`${record.deal_id}-${record.line_item_name}-${record.product}-${record.change_type}-${record.revenue_kind}`}
                            className={`${record.change_type === "ended" ? "opacity-60" : ""} ${record.revenue_kind === "projected" ? "bg-violet-50/40" : ""}`}
                            data-testid={`month-drilldown-row-${record.deal_id}`}
                          >
                            {activeColumns.map((col) => renderCell(col, record))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )
          ) : (
            <div className="rounded-md border border-[#cccccc] bg-[#f5f5f5] px-6 py-16 text-center text-sm text-[#4a5b68]">
              Select a month in the chart to inspect the contributing records.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
